/*
 * Registertabelle eines Geräts (docs/servicezugang-oem-und-fern.md, Teil 4):
 * der volle Adressraum für den Servicezugang, nicht das Betriebsschema.
 * Hier nur die Logik ohne Oberfläche: Zeilen aus den Adressen, Filter,
 * Ebene und Schreibbarkeit ändern, CSV zum Bearbeiten in Excel ausgeben.
 * Die CSV liest der Server-Import wieder ein (gleiche Spaltennamen).
 */

/** Gleich wie normalizeSearch in serviceRemoteModel.ts. Kopie, weil die
 *  Modelltests unter node --experimental-strip-types laufen und dort ein
 *  relativer Import ohne Endung nicht aufgelöst wird. Ändert sich eine,
 *  muss die andere mit. */
function normalizeSearch(text: string): string {
  return text
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^\p{L}\p{N}]/gu, ' ')
    .replace(/ae/g, 'a')
    .replace(/oe/g, 'o')
    .replace(/ue/g, 'u');
}

export interface RawEnumMember { value: number; name: string; displayName?: string | null }
export interface RawAddress {
  address: string;
  symbol?: string | null;
  valueType?: string | null;
  metadata?: Record<string, string> | null;
  enumMembers?: RawEnumMember[] | null;
  path?: string[] | null;
}

export const LEVELS = ['endbenutzer', 'inbetriebsetzung', 'fachmann', 'oem'] as const;
export type Level = typeof LEVELS[number];
export const LEVEL_NAMES: Record<Level, string> = {
  endbenutzer: 'Endbenutzer',
  inbetriebsetzung: 'Inbetriebsetzung',
  fachmann: 'Fachmann',
  oem: 'OEM',
};

export interface RegisterRow {
  address: string;
  label: string;
  group: string;
  line: string;
  unit: string;
  min: string;
  max: string;
  step: string;
  /** Ohne Angabe gilt OEM (Vertrag). */
  level: Level;
  writable: boolean;
  valueType: string;
}

const meta = (a: RawAddress, key: string) => a.metadata?.[key]?.trim() ?? '';

export function toRow(a: RawAddress): RegisterRow {
  const level = meta(a, 'oem.level').toLowerCase();
  return {
    address: a.address,
    label: meta(a, 'oem.label') || a.symbol || a.address,
    group: meta(a, 'oem.group') || (a.path?.length ? a.path.join(' / ') : 'Sonstiges'),
    line: meta(a, 'oem.line'),
    unit: meta(a, 'oem.unit'),
    min: meta(a, 'oem.min'),
    max: meta(a, 'oem.max'),
    step: meta(a, 'oem.step'),
    level: (LEVELS as readonly string[]).includes(level) ? level as Level : 'oem',
    writable: meta(a, 'oem.writable').toLowerCase() === 'true',
    valueType: a.valueType ?? '',
  };
}

/** Jedes Wort muss in Adresse, Nummer, Bezeichnung oder Gruppe vorkommen;
 *  Umlaute gleich wie ihre Umschreibung, wie die Suche am Panel. */
export function filterRows(rows: RegisterRow[], query: string, level: Level | '' = ''): RegisterRow[] {
  const words = normalizeSearch(query).split(' ').filter(Boolean);
  return rows.filter((r) => {
    if (level && r.level !== level) return false;
    if (words.length === 0) return true;
    const hay = normalizeSearch(`${r.address} ${r.line} ${r.label} ${r.group}`);
    return words.every((w) => hay.includes(w));
  });
}

/** Ebene oder Schreibbarkeit einer Adresse setzen; liefert eine neue Liste. */
export function withHint(addresses: RawAddress[], address: string, key: 'oem.level' | 'oem.writable', value: string): RawAddress[] {
  return addresses.map((a) => a.address !== address ? a : { ...a, metadata: { ...(a.metadata ?? {}), [key]: value } });
}

const FIXED: { header: string; get(a: RawAddress): string }[] = [
  { header: 'Adresse', get: (a) => a.address },
  { header: 'Bezeichnung', get: (a) => meta(a, 'oem.label') },
  { header: 'Gruppe', get: (a) => meta(a, 'oem.group') },
  { header: 'Bediennummer', get: (a) => meta(a, 'oem.line') },
  { header: 'Einheit', get: (a) => meta(a, 'oem.unit') },
  { header: 'Min', get: (a) => meta(a, 'oem.min') },
  { header: 'Max', get: (a) => meta(a, 'oem.max') },
  { header: 'Schritt', get: (a) => meta(a, 'oem.step') },
  { header: 'Ebene', get: (a) => meta(a, 'oem.level') },
  { header: 'schreibbar', get: (a) => meta(a, 'oem.writable') === '' ? '' : meta(a, 'oem.writable') === 'true' ? 'ja' : 'nein' },
  { header: 'Typ', get: (a) => a.valueType ?? '' },
  { header: 'Symbol', get: (a) => a.symbol ?? '' },
];

function cell(v: string): string {
  return /[;"\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/** CSV mit Semikolon, so wie Excel sie im deutschen Gebietsschema öffnet.
 *  Treiberangaben (alles ausser oem.*) als eigene Spalten mit ihrem
 *  Schlüssel; der Import reicht unbekannte Spalten unverändert durch. */
export function toCsv(addresses: RawAddress[]): string {
  const extra = [...new Set(addresses.flatMap((a) => Object.keys(a.metadata ?? {}).filter((k) => !k.startsWith('oem.'))))].sort();
  const lines = [[...FIXED.map((f) => f.header), ...extra].map(cell).join(';')];
  for (const a of addresses) {
    lines.push([...FIXED.map((f) => f.get(a)), ...extra.map((k) => a.metadata?.[k] ?? '')].map(cell).join(';'));
  }
  return '﻿' + lines.join('\r\n') + '\r\n';
}

/** Anzahl je Ebene, für die Übersicht. */
export function countByLevel(rows: RegisterRow[]): Record<Level, number> {
  const out: Record<Level, number> = { endbenutzer: 0, inbetriebsetzung: 0, fachmann: 0, oem: 0 };
  for (const r of rows) out[r.level]++;
  return out;
}
